import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Building2, CheckCircle } from "lucide-react";

const patientBenefits = [
  "Understand exactly what was implanted during your procedure",
  "Receive an objective estimate of your graft count",
  "View implantation density distribution across the scalp",
  "Get a professional PDF report to keep with your medical records",
  "Make more informed decisions when reviewing your results with your surgeon",
  "Bring documented evidence to any follow-up consultation",
];

const clinicBenefits = [
  "Add an objective quality control layer to post-operative workflows",
  "Generate professional patient-facing reports automatically",
  "Differentiate your clinic through a transparency-first approach",
  "Support internal review and documentation of procedure outcomes",
  "Build long-term patient trust with data-backed communication",
  "Operate with standardized post-operative reporting practices",
];

const tabs = [
  { key: "patient", label: "For Patients", icon: User },
  { key: "clinic", label: "For Clinics", icon: Building2 },
];

const ForWhom = () => {
  const [active, setActive] = useState("patient");

  const benefits = active === "patient" ? patientBenefits : clinicBenefits;

  return (
    <section
      id="for-whom"
      className="relative py-24 sm:py-32 overflow-hidden bg-[#060C18]"
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 50%, rgba(37,99,235,0.08) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs font-medium tracking-[0.1em] uppercase mb-6">
            Who It's For
          </div>
          <h2
            className="text-white font-semibold tracking-tight leading-tight"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}
          >
            Built for patients and clinics alike
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed mt-4 max-w-xl mx-auto">
            Whether you're a patient seeking clarity or a clinic building a
            stronger standard of care, Graft Counter is designed for you.
          </p>
        </motion.div>

        {/* Tab Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex justify-center mb-10"
        >
          <div className="flex bg-white/5 border border-white/10 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActive(tab.key)}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  active === tab.key
                    ? "bg-blue-600 text-white shadow-lg"
                    : "text-white/45 hover:text-white/75"
                }`}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7 sm:p-10"
          >
            {/* Panel header */}
            <div className="flex items-start gap-4 mb-8 pb-7 border-b border-white/8">
              <div className="w-12 h-12 bg-blue-500/15 border border-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                {active === "patient" ? (
                  <User className="w-6 h-6 text-blue-400 stroke-[1.5]" />
                ) : (
                  <Building2 className="w-6 h-6 text-blue-400 stroke-[1.5]" />
                )}
              </div>
              <div>
                <h3 className="text-white font-semibold text-xl mb-1">
                  {active === "patient" ? "For Patients" : "For Clinics"}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {active === "patient"
                    ? "Understand and document your procedure outcome with objective AI analysis"
                    : "Add a verifiable quality control and reporting layer to your post-operative workflow"}
                </p>
              </div>
            </div>

            {/* Benefits list */}
            <div className="grid sm:grid-cols-2 gap-3.5">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.06 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-4 h-4 text-blue-400 stroke-[1.8] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300 text-sm leading-relaxed">
                    {benefit}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ForWhom;
